import { z } from "zod";

import { LI_POST_QUERY_MAX_LENGTH } from "@signalist/shared-types";

import { MetaDataSchema } from "@/schemas/meta-data";

import { LiPostContentType, LiPostResultStatus, LiPostSort, TimeType } from "@/enum/li-post.enum";
import { ListStatus } from "@/enum/list.enum";

import { LiPostType } from "@/enum/liprofile.enum";

import { SuggestionSchema } from "../../LiPeople/schema/liPeople";
import { LiPostResultSchema } from "./liPostResult";

/** Shown at the topic builder and by the form schema when the composed query is over the limit. */
export const LI_POST_QUERY_TOO_LONG_MESSAGE = "This topic is too long; remove some keywords";

const topicQuerySchema = () =>
	z.string().max(LI_POST_QUERY_MAX_LENGTH, LI_POST_QUERY_TOO_LONG_MESSAGE);

/** BY_URL drops `query` before it is sent, so a leftover topic must not block that source. */
const queryLimitApplies = (type: LiPostType) => type !== LiPostType.BY_URL;

export const LiPostSchema = z.object({
	id: z.number(),
	name: z.string().optional(),
	query: topicQuerySchema().optional().nullable(),
	postUrl: z.string().optional(),
	sortBy: z.nativeEnum(LiPostSort),
	authorJobTitle: z.string().trim().nullable(),
	contentType: z.nativeEnum(LiPostContentType).nullable(),
	fromMember: SuggestionSchema.optional().nullable(),
	fromOrganization: SuggestionSchema.optional().nullable(),
	authorCompany: SuggestionSchema.optional().nullable(),
	authorIndustry: SuggestionSchema.optional().nullable(),
	mentionsMember: SuggestionSchema.optional().nullable(),
	mentionsOrganization: SuggestionSchema.optional().nullable(),
	status: z.nativeEnum(LiPostResultStatus),
	activeStatus: z.nativeEnum(ListStatus),
	liPostResults: z.array(LiPostResultSchema),
	repeatDaily: z.boolean(),
	timeType: z.nativeEnum(TimeType),
	timeFrame: z.number().optional(),
	timeStart: z.date().optional(),
	timeEnd: z.date().optional(),
	listId: z.number().optional(),
	type: z.nativeEnum(LiPostType),
	maxPerPost: z.number().optional(),
	posts: z.boolean(),
	commentsToPost: z.boolean(),
	likesToPost: z.boolean(),
	likesToComment: z.boolean(),
	commentsToComment: z.boolean(),
	plannedScrapingUserId: z.number().optional().nullable(),
});

export const CreateLiPostSchema = z
	.object({
		id: z.number().optional(),
		query: z.string().optional().nullable(),
		postUrl: z.string().optional(),
		sortBy: z.nativeEnum(LiPostSort),
		authorJobTitle: z.string().trim().optional(),
		contentType: z.nativeEnum(LiPostContentType).nullable().optional(),
		fromMember: SuggestionSchema.optional().nullable(),
		fromOrganization: SuggestionSchema.optional().nullable(),
		authorCompany: SuggestionSchema.optional().nullable(),
		authorIndustry: SuggestionSchema.optional().nullable(),
		mentionsMember: SuggestionSchema.optional().nullable(),
		mentionsOrganization: SuggestionSchema.optional().nullable(),
		repeatDaily: z.boolean(),
		timeType: z.nativeEnum(TimeType),
		timeFrame: z.number().optional(), // num of days or 'period'
		timeStart: z.date().optional(),
		timeEnd: z.date().optional(),
		listId: z.number().optional(),
		type: z.nativeEnum(LiPostType),
		maxPerPost: z.number().optional(),
		posts: z.boolean(),
		commentsToPost: z.boolean(),
		likesToPost: z.boolean(),
		likesToComment: z.boolean(),
		commentsToComment: z.boolean(),
		plannedScrapingUserId: z.number().optional().nullable(),
	})
	.refine(
		(data) => {
			if (data.type === LiPostType.BY_TOPIC && !data.query) {
				return false;
			}

			return true;
		},
		{
			message: "REQUIRED",
			path: ["query"],
		}
	)
	.refine(
		(data) =>
			!queryLimitApplies(data.type) || (data.query?.length ?? 0) <= LI_POST_QUERY_MAX_LENGTH,
		{
			message: LI_POST_QUERY_TOO_LONG_MESSAGE,
			path: ["query"],
		}
	)
	.refine(
		(data) => {
			if (
				data.type === LiPostType.BY_URL &&
				data.postUrl &&
				!data.postUrl?.includes("www.linkedin.com/posts")
			) {
				return false;
			}

			return true;
		},
		{
			message: "INVALID_URL",
			path: ["postUrl"],
		}
	)
	.refine(
		(data) => {
			if (data.type === LiPostType.BY_URL && !data.postUrl) {
				return false;
			}

			return true;
		},
		{
			message: "REQUIRED",
			path: ["postUrl"],
		}
	)
	.refine(
		(data) => {
			if (data.postUrl) {
				return true;
			}

			if (data.timeFrame && data.timeFrame > 0) {
				return true;
			}
		},
		{
			message: "REQUIRED",
			path: ["timeFrame"],
		}
	)
	.refine(
		(data) => {
			if (
				data.type === LiPostType.BY_TOPIC &&
				!data.posts &&
				!data.likesToPost &&
				!data.commentsToPost
			) {
				return false;
			}

			return true;
		},
		{
			message: "REQUIRED",
			path: ["posts"],
		}
	)
	.refine(
		(data) => {
			if (
				data.type === LiPostType.BY_TOPIC &&
				!data.posts &&
				!data.likesToPost &&
				!data.commentsToPost
			) {
				return false;
			}

			if (data.type === LiPostType.BY_URL && !data.likesToPost && !data.commentsToPost) {
				return false;
			}

			if (
				data.type === LiPostType.BY_PROFILE &&
				data.fromOrganization &&
				!data.likesToPost &&
				!data.commentsToPost
			) {
				return false;
			}

			if (
				data.type === LiPostType.BY_PROFILE &&
				data.fromMember &&
				!data.likesToPost &&
				!data.commentsToPost &&
				!data.likesToComment &&
				!data.commentsToComment
			) {
				return false;
			}

			return true;
		},
		{
			message: "REQUIRED",
			path: ["likesToPost"],
		}
	)
	.refine(
		(data) => {
			if (
				data.type === LiPostType.BY_TOPIC &&
				!data.posts &&
				!data.likesToPost &&
				!data.commentsToPost
			) {
				return false;
			}

			if (data.type === LiPostType.BY_URL && !data.likesToPost && !data.commentsToPost) {
				return false;
			}

			if (
				data.type === LiPostType.BY_PROFILE &&
				data.fromOrganization &&
				!data.likesToPost &&
				!data.commentsToPost
			) {
				return false;
			}

			if (
				data.type === LiPostType.BY_PROFILE &&
				data.fromMember &&
				!data.likesToPost &&
				!data.commentsToPost &&
				!data.likesToComment &&
				!data.commentsToComment
			) {
				return false;
			}

			return true;
		},
		{
			message: "REQUIRED",
			path: ["commentsToPost"],
		}
	)
	.refine(
		(data) => {
			if (
				data.type === LiPostType.BY_PROFILE &&
				data.fromMember &&
				!data.likesToPost &&
				!data.commentsToPost &&
				!data.likesToComment &&
				!data.commentsToComment
			) {
				return false;
			}

			return true;
		},
		{
			message: "REQUIRED",
			path: ["likesToComment"],
		}
	)
	.refine(
		(data) => {
			if (data.type === LiPostType.BY_PROFILE && !data.fromMember && !data.fromOrganization) {
				return false;
			}

			return true;
		},
		{
			message: "REQUIRED",
			path: ["fromMember"],
		}
	)
	.refine(
		(data) => {
			if (data.type === LiPostType.BY_PROFILE && !data.fromMember && !data.fromOrganization) {
				return false;
			}

			return true;
		},
		{
			message: "REQUIRED",
			path: ["fromOrganization"],
		}
	)
	.refine(
		(data) => {
			if (
				data.type === LiPostType.BY_PROFILE &&
				data.fromMember &&
				!data.likesToPost &&
				!data.commentsToPost &&
				!data.likesToComment &&
				!data.commentsToComment
			) {
				return false;
			}

			return true;
		},
		{
			message: "REQUIRED",
			path: ["commentsToComment"],
		}
	);

export const LiPostResponseSchema = z.object({
	data: z.array(LiPostSchema),
	meta: MetaDataSchema,
});

export const ValidatePostUrlResponseSchema = z.object({
	shareUrl: z.string(),
	commentary: z.string().nullable(),
	actorName: z.string().nullable(),
	actorDescription: z.string().nullable(),
	actorImage: z.string().nullable(),
	actorProfileUrl: z.string().nullable(),
	numLikes: z.number(),
	numComments: z.number(),
	numShares: z.number(),
	postedAt: z.string().nullable(),
});

export type LiPost = z.infer<typeof LiPostSchema>;

export type CreateLiPost = z.infer<typeof CreateLiPostSchema>;

export type LiPostResponse = z.infer<typeof LiPostResponseSchema>;

export type ValidatePostUrlResponse = z.infer<typeof ValidatePostUrlResponseSchema>;
